import { Product } from "../../../../common/api/decimal/methods/product.ts";
import { useAssetDescriptors } from "../../../../common/features/asset/useAssetDescriptors.ts";
import { SystemKind } from "../../../../common/api/decimal/common-types/kind.ts";
import { useMemo } from "react";
import { useIntl } from "react-intl";
import { getProductTypeParam } from "../../product/productLoader.ts";
import { CourseMaterial } from "./coursesLoader.ts";
import { getStoreProducts } from "./common/storeProductUtils.ts";
import { isOutOfStock } from "../../product/common/productUtils.ts";

export type FormatType = "digital" | "print";

interface FormatGroup {
  title: string;
  product: Product;
  type: FormatType;
  optionCount: number;
}

export default function useStoreProduct(material: CourseMaterial) {
  const intl = useIntl();
  const { product, vbid } = material;
  const { digitalProduct, printProduct, isPrintOutOfStock, ...rest } =
    getStoreProducts(product);
  const digitalParts = useAssetDescriptors({
    kind: digitalProduct?.kind ?? ("" as SystemKind),
  });
  const digitalDescriptor = digitalParts.join(" + ");
  const formatGroups: FormatGroup[] = useMemo(() => {
    // note product say max two tabs for my courses use
    const groups: FormatGroup[] = [];

    if (digitalProduct?.variants.length) {
      groups.push({
        title: digitalDescriptor,
        product: digitalProduct,
        type: "digital",
        optionCount: digitalProduct.variants.length,
      });
    }

    // adopted access codes and bundles have priority and should eliminate potential print products
    const isOnlineResource =
      digitalProduct?.kind === "Online Resource Bundle" ||
      digitalProduct?.kind === "Online Resource";

    if (
      printProduct?.variants.length &&
      !isOnlineResource &&
      !isPrintOutOfStock
    ) {
      groups.push({
        title: intl.formatMessage({ defaultMessage: "Print" }),
        product: printProduct,
        type: "print",
        optionCount: printProduct.variants.filter(
          (variant) => !isOutOfStock(variant)
        ).length,
      });
    }

    return groups;
  }, [
    digitalDescriptor,
    digitalProduct,
    intl,
    isPrintOutOfStock,
    printProduct,
  ]);

  const scope =
    formatGroups.length === 0 && printProduct?.variants.length
      ? "print"
      : formatGroups.map((group) => getProductTypeParam(group.product)).join();

  return useMemo(
    () => ({
      formatGroups,
      pdpLink: `products/${vbid}?scope=${scope}`,
      ...rest,
    }),
    [formatGroups, rest, scope, vbid]
  );
}
